import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { ActionId } from "@redotech/redo-web/actions/action-ids";
import { useActionScope } from "@redotech/redo-web/actions/action-scope-context";
import {
  isSubmenuTrigger,
  type ScopeNode,
} from "@redotech/redo-web/actions/scope-tree";
import { RedoCommandShortcut } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-shortcut";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { getShortcutDisplayParts } from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import type { ShopperAiScope } from "@redotech/redo-web/keyboard-shortcuts/shopper-ai-shortcuts";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import { memo, useCallback, useMemo, useState } from "react";
import { getAction } from "./action-registry";
import type { SupportTicketActionHandlerMap } from "./use-support-ticket-actions";

interface ScopeMenuProps {
  /** The scope node to render */
  scope: ScopeNode<ActionId, ShopperAiScope>;
  /** Map of action IDs to bound handler functions */
  handlers: SupportTicketActionHandlerMap;
  /** The conversation for visibility/enabled checks */
  conversation: SupportTicketListItemResponse;
  /** Called when the menu should close (after action execution) */
  onClose: () => void;
  /** Nesting depth for styling (0 = root) */
  depth?: number;
}

/**
 * Recursive menu component that renders a scope and its children.
 * Uses the scope tree structure to determine menu hierarchy.
 */
export const ScopeMenu = memo(function ScopeMenu({
  scope,
  handlers,
  conversation,
  onClose,
  depth = 0,
}: ScopeMenuProps) {
  const [openChildId, setOpenChildId] = useState<string | null>(null);
  const { pushScope, popScope } = useActionScope();

  const handleCloseChild = useCallback(() => {
    setOpenChildId(null);
  }, []);

  const handleCloseAll = useCallback(() => {
    setOpenChildId(null);
    onClose();
  }, [onClose]);

  // Build keyboard shortcut bindings from scope's action refs
  const bindings = useMemo(() => {
    return scope.actionRefs
      .map((ref) => {
        const action = getAction(ref.actionId);
        if (!action.shortcut) {
          return null;
        }

        return {
          shortcut: action.shortcut,
          handler: () => {
            if (isSubmenuTrigger(ref)) {
              setOpenChildId(ref.childScope.id);
            } else {
              handlers[ref.actionId]?.();
              handleCloseAll();
            }
          },
        };
      })
      .filter(
        (binding): binding is NonNullable<typeof binding> => binding !== null,
      );
  }, [scope.actionRefs, handlers, handleCloseAll]);

  // Scope management for keyboard shortcuts - captures scope in closure
  const scopeManagement = useMemo(
    () => ({ pushScope: () => pushScope(scope), popScope }),
    [pushScope, popScope, scope],
  );

  useKeyboardShortcuts({
    bindings,
    scope: scope.id,
    enabled: openChildId === null,
    onEscape: depth > 0 ? handleCloseChild : undefined,
    scopeManagement: depth > 0 ? scopeManagement : undefined,
  });

  return (
    <>
      {scope.actionRefs.map((ref) => {
        const action = getAction(ref.actionId);

        // Check visibility
        if (action.isVisible && !action.isVisible(conversation)) {
          return null;
        }

        // Check if enabled
        const isEnabled = action.isEnabled
          ? action.isEnabled(conversation)
          : true;

        if (isSubmenuTrigger(ref)) {
          // Render as submenu trigger with recursive child
          return (
            <DropdownMenuSub
              key={ref.actionId}
              onOpenChange={(open) =>
                setOpenChildId(open ? ref.childScope.id : null)
              }
              open={openChildId === ref.childScope.id}
            >
              <DropdownMenuSubTrigger disabled={!isEnabled}>
                <div className="flex w-full items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    {action.Icon && <action.Icon className="h-4 w-4" />}
                    <span>{action.title}</span>
                  </div>
                  {action.shortcut && (
                    <RedoCommandShortcut
                      hotkey={getShortcutDisplayParts(action.shortcut)}
                    />
                  )}
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <ScopeMenu
                  conversation={conversation}
                  depth={depth + 1}
                  handlers={handlers}
                  onClose={handleCloseAll}
                  scope={ref.childScope}
                />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          );
        }

        // Render as leaf action
        return (
          <DropdownMenuItem
            disabled={!isEnabled}
            key={ref.actionId}
            onClick={() => {
              handlers[ref.actionId]?.();
              handleCloseAll();
            }}
          >
            <div className="flex w-full items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                {action.Icon && <action.Icon className="h-4 w-4" />}
                <span>{action.title}</span>
              </div>
              {action.shortcut && (
                <RedoCommandShortcut
                  hotkey={
                    depth === 0
                      ? getShortcutDisplayParts(action.shortcut)
                      : [action.shortcut.keyLabel]
                  }
                />
              )}
            </div>
          </DropdownMenuItem>
        );
      })}
    </>
  );
});
